import { Add as AddIcon, DeleteOutline as DeleteIcon, Link as LinkIcon } from "@mui/icons-material";
import { IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { Signal, useSignal, useSignalEffect } from "@preact/signals-react";
import { debounce, isEqual, isNil } from "lodash-es";
import { ChangeEvent, Fragment, useRef } from "react";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { INGAnalyticsEditorProps } from "../../library/NGFieldExtensions";

const DEBOUNCE_TIMEOUT = 300; // milliseconds

const KEY_EVENT_NAME = "EventName";
const KEY_EVENT_CATEGORY = "EventCategory";
const KEY_METADATA = "Metadata";

/* eslint-disable @typescript-eslint/no-explicit-any */
interface AnalyticsValue {
  [KEY_EVENT_NAME]?: string;
  [KEY_EVENT_CATEGORY]?: string;
  [KEY_METADATA]?: {
    [key: string]: any;
    Bindings?: Record<string, string>;
  };
  [key: string]: any;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const debouncedHandler = debounce((fn, event, value) => {
  if (!isNil(fn)) {
    fn(event, value);
  }
}, DEBOUNCE_TIMEOUT);

export function NGAnalyticsEditor({ config, context }: INGAnalyticsEditorProps) {
  const local = setupLocalState(
    config,
    {
      Value: useSignal(config.Value ?? {}) as Signal<AnalyticsValue>,
    },
    context
  );

  const handlers = setupHandlers(config, context);

  // refs
  const previousValueRef = useRef<Record<string, unknown>>(sanitizeValue(local.Value.value));

  // variables
  const bindings = local.Value.value.Bindings || {};
  const bindingEventName = bindings[KEY_EVENT_NAME] || "";
  const bindingEventCategory = bindings[KEY_EVENT_CATEGORY] || "";

  const metadataBindings = local.Value.value[KEY_METADATA]?.Bindings ?? {};
  const metadata = local.Value.value[KEY_METADATA] ?? {};
  const { Bindings: _bindings, ...metadataWithoutBindings } = metadata;
  const pairs = Object.entries(metadataWithoutBindings).map(([key, value]) => ({ key, value }));
  const initialPairs = pairs.length > 0 ? pairs : [{ key: "", value: "" }];
  const initialBindingExpressions = initialPairs.map((pair) => metadataBindings[pair.key] || "");

  // Initialize visible bindings for keys that have existing binding expressions
  const initialVisibleBindings = initialPairs
    .map((pair, index) => (metadataBindings[pair.key] ? index : -1))
    .filter((index) => index !== -1);

  // signals
  const kvPairs = useSignal(initialPairs);
  const kvPairBindings = useSignal<string[]>(initialBindingExpressions);
  const kvPairVisibleBindings = useSignal<number[]>(initialVisibleBindings);
  const isVisibleBindingEventName = useSignal(!!local.Value.value.Bindings?.[KEY_EVENT_NAME]);
  const isVisibleBindingEventCategory = useSignal(!!local.Value.value.Bindings?.[KEY_EVENT_CATEGORY]);

  // variables
  const hasEmptyPairs = kvPairs.value.some((pair) => pair.key?.trim() === "" && String(pair.value)?.trim() === "");
  const canAdd = !hasEmptyPairs;
  // can delete if there are more than one pair, or if the last pair is not empty
  const canDelete = kvPairs.value.length > 1 || (kvPairs.value.length === 1 && kvPairs.value[0].key?.trim() !== "");

  // helpers
  function sanitizeValue(value: AnalyticsValue): AnalyticsValue {
    const sanitized: AnalyticsValue = {
      ...value,
      [KEY_EVENT_NAME]: value[KEY_EVENT_NAME] || "",
      [KEY_EVENT_CATEGORY]: value[KEY_EVENT_CATEGORY] || "",
      [KEY_METADATA]: value[KEY_METADATA] ?? {},
      Bindings: value.Bindings ?? {},
    };

    // 1. Filter out any empty metadata entries (key/value both blank)
    if (sanitized[KEY_METADATA]) {
      const filteredMeta = Object.fromEntries(
        Object.entries(sanitized[KEY_METADATA] as Record<string, unknown>).filter(
          ([k, v]) => k.trim() !== "" || String(v).trim() !== ""
        )
      );
      sanitized[KEY_METADATA] = filteredMeta;
    }

    // 2. Filter out any empty Binding entries
    if (sanitized.Bindings) {
      const filteredBindings = Object.fromEntries(
        Object.entries(sanitized.Bindings).filter(([k, v]) => k.trim() !== "" && String(v).trim() !== "")
      );
      sanitized.Bindings = filteredBindings;
    }

    // 3. Filter out any empty metadata‐level Bindings
    if (sanitized[KEY_METADATA]?.Bindings && typeof sanitized[KEY_METADATA].Bindings === "object") {
      const filteredMetaBindings = Object.fromEntries(
        Object.entries(sanitized[KEY_METADATA].Bindings).filter(([k, v]) => k.trim() !== "" && String(v).trim() !== "")
      );
      sanitized[KEY_METADATA].Bindings = filteredMetaBindings;
    }

    // 4. If Metadata has no keys left, delete it entirely
    if (sanitized[KEY_METADATA] && Object.keys(sanitized[KEY_METADATA] as object).length === 0) {
      delete sanitized[KEY_METADATA];
    }

    // 5. If top‐level Bindings has no keys left, delete it entirely
    if (sanitized.Bindings && Object.keys(sanitized.Bindings).length === 0) {
      delete sanitized.Bindings;
    }

    return sanitized;
  }

  // event handlers
  const toggleBindingVisibility = (index: number) => {
    if (kvPairVisibleBindings.value.includes(index)) {
      kvPairVisibleBindings.value = kvPairVisibleBindings.value.filter((i) => i !== index);
    } else {
      kvPairVisibleBindings.value = [...kvPairVisibleBindings.value, index];
    }
  };

  const handleAdd = () => {
    if (hasEmptyPairs) return;
    kvPairs.value = [...kvPairs.value, { key: "", value: "" }];
    kvPairBindings.value = [...kvPairBindings.value, ""];
  };

  const handleRemove = (idx: number) => {
    // don't delete the last pair, just clear it
    if (kvPairs.value.length <= 1) {
      kvPairs.value = [{ key: "", value: "" }];
      kvPairBindings.value = [""];
      kvPairVisibleBindings.value = [];
    } else {
      kvPairs.value = kvPairs.value.filter((_, i) => i !== idx);
      kvPairBindings.value = kvPairBindings.value.filter((_, i) => i !== idx);
      kvPairVisibleBindings.value = kvPairVisibleBindings.value
        .filter((visibleIdx) => visibleIdx !== idx)
        .map((visibleIdx) => (visibleIdx > idx ? visibleIdx - 1 : visibleIdx));
    }
  };

  const handleBindingExpressionChange = ({
    event,
    index,
  }: {
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    index: number;
  }) => {
    const value = event.target.value;

    kvPairBindings.value = kvPairBindings.value.map((expr, i) => (i === index ? value : expr));

    if (kvPairBindings.value.length < kvPairs.value.length) {
      kvPairBindings.value = [
        ...kvPairBindings.value,
        ...Array(kvPairs.value.length - kvPairBindings.value.length).fill(""),
      ];
    }
  };

  const handleChangeMetadata = ({
    event,
    index,
    field,
  }: {
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    index: number;
    field: "key" | "value";
  }) => {
    const value = event.target.value;
    kvPairs.value = kvPairs.value.map((pair, i) => (i === index ? { ...pair, [field]: value } : pair));
  };

  // effects
  useSignalEffect(() => {
    const current = sanitizeValue(local.Value.value);
    if (isEqual(current, previousValueRef.current)) return;
    previousValueRef.current = current;
    debouncedHandler(handlers["onChange"], new Event("onChange"), current);
  });

  // metadata and bindings update effect
  useSignalEffect(() => {
    // Build metadata object from pairs with non-empty keys
    const metadata = Object.fromEntries(
      kvPairs.value.filter((pair) => pair.key.trim()).map((pair) => [pair.key, pair.value])
    );

    // Build metadata bindings object
    const metadataBindings = Object.fromEntries(
      kvPairs.value
        .map((pair, index) => ({ key: pair.key, binding: kvPairBindings.value[index] }))
        .filter(({ key, binding }) => key?.trim() && binding?.trim())
        .map(({ key, binding }) => [key, binding])
    );

    // Update the main value object with metadata and its bindings
    const newMetadata = { ...metadata };
    if (Object.keys(metadataBindings).length > 0) {
      newMetadata.Bindings = metadataBindings;
    }

    if (isEqual(newMetadata, local.Value.value[KEY_METADATA])) return;

    local.Value.value = {
      ...local.Value.value,
      [KEY_METADATA]: newMetadata,
    };
  });

  // render
  return (
    <Stack direction="column" sx={{ width: "100%" }} gap={1}>
      {/* event name */}
      <Stack direction="column" flex={1}>
        <Typography className="control-label">Event Name</Typography>
        <Stack direction="row" alignItems="center">
          <TextField
            sx={{ marginTop: "2px" }}
            placeholder="i.e. button_click, form_submit"
            value={local.Value.value[KEY_EVENT_NAME] ?? ""}
            onChange={(e) => {
              local.Value.value = { ...local.Value.value, [KEY_EVENT_NAME]: e.target.value };
            }}
          />
          <Tooltip placement="top" title="Show/hide binding expression for 'Event Name'">
            <IconButton
              color="primary"
              onClick={() => (isVisibleBindingEventName.value = !isVisibleBindingEventName.value)}
            >
              <LinkIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        {isVisibleBindingEventName.value && (
          <TextField
            label="'Event Name' binding expression"
            value={bindingEventName}
            onChange={(e) => {
              const newBindings = { ...local.Value.value.Bindings, [KEY_EVENT_NAME]: e.target.value };
              local.Value.value = { ...local.Value.value, Bindings: newBindings };
            }}
          />
        )}
      </Stack>

      {/* event category */}
      <Stack direction="column" flex={1}>
        <Typography className="control-label">Event Category</Typography>
        <Stack direction="row" alignItems="center">
          <TextField
            sx={{ marginTop: "2px" }}
            placeholder="i.e. engagement, navigation, user_interaction"
            value={local.Value.value[KEY_EVENT_CATEGORY] ?? ""}
            onChange={(e) => {
              local.Value.value = { ...local.Value.value, [KEY_EVENT_CATEGORY]: e.target.value };
            }}
          />
          <Tooltip placement="top" title="Show/hide binding expression for 'Event Category'">
            <IconButton
              color="primary"
              onClick={() => (isVisibleBindingEventCategory.value = !isVisibleBindingEventCategory.value)}
            >
              <LinkIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        {isVisibleBindingEventCategory.value && (
          <TextField
            label="'Event Category' binding expression"
            value={bindingEventCategory}
            onChange={(e) => {
              const newBindings = { ...local.Value.value.Bindings, [KEY_EVENT_CATEGORY]: e.target.value };
              local.Value.value = { ...local.Value.value, Bindings: newBindings };
            }}
          />
        )}
      </Stack>

      {/* metadata */}
      <Stack direction="column">
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
          <Typography className="control-label">Metadata</Typography>
          <IconButton color="primary" disabled={!canAdd} onClick={handleAdd}>
            <Tooltip placement="top" title="Add a new metadata key-value pair">
              <AddIcon fontSize="small" />
            </Tooltip>
          </IconButton>
        </Stack>
        <Stack direction="column" gap={1}>
          {kvPairs.value.map((pair, index) => (
            <Fragment key={index}>
              <Stack direction="row" gap={1} alignItems="center">
                <TextField
                  sx={{ marginTop: 0 }}
                  size="small"
                  placeholder="Key"
                  value={pair.key}
                  onChange={(event) => handleChangeMetadata({ event, index, field: "key" })}
                />
                <TextField
                  sx={{ marginTop: 0 }}
                  size="small"
                  placeholder="Value"
                  value={pair.value}
                  onChange={(event) => handleChangeMetadata({ event, index, field: "value" })}
                />
                <Stack direction="row" alignItems="center">
                  <Tooltip placement="top" title="Show/hide binding expression for this key">
                    <IconButton color="primary" onClick={() => toggleBindingVisibility(index)}>
                      <LinkIcon fontSize="medium" />
                    </IconButton>
                  </Tooltip>
                  <IconButton disabled={!canDelete} color="primary" onClick={() => handleRemove(index)} size="small">
                    <Tooltip placement="top" title="Delete this metadata pair">
                      <DeleteIcon fontSize="medium" />
                    </Tooltip>
                  </IconButton>
                </Stack>
              </Stack>

              {/* binding expression */}
              {kvPairVisibleBindings.value.includes(index) && (
                <Stack direction="row" gap={1} alignItems="center">
                  <TextField
                    label="'Value' Binding Expression"
                    sx={{ marginTop: 0 }}
                    size="small"
                    value={kvPairBindings.value[index] || ""}
                    onChange={(event) => handleBindingExpressionChange({ event, index })}
                  />
                </Stack>
              )}
            </Fragment>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
}
